import { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { type TabItem, type TabItemId } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const useLocationSearchPredictionsTabs = (params: Params) => {
  const { predictionsCount, restaurantsCount } = params;

  const { formatMessage } = useIntl();

  // ─── State ───────────────────────────────────────────────────────────────────

  const [activeTabId, setActiveTabId] = useState<TabItemId>(LOCATIONS_TAB_ID);

  // ─── Flags ───────────────────────────────────────────────────────────────────

  const hasPredictions = predictionsCount > 0;
  const hasRestaurants = restaurantsCount > 0;
  const shouldRenderTabBar = hasPredictions || hasRestaurants;

  const isLocationsTabActive = activeTabId === LOCATIONS_TAB_ID;
  const isRestaurantsTabActive = activeTabId === RESTAURANTS_TAB_ID;

  // ─── Tabs ────────────────────────────────────────────────────────────────────

  const locationsTab = useMemo<TabItem>(() => {
    const label = formatMessage(messages.locations, { count: predictionsCount }); // prettier-ignore

    return {
      id: LOCATIONS_TAB_ID,
      panelId: LOCATIONS_TAB_PANEL_ID,
      label,
      isDisabled: !hasPredictions,
    };
  }, [formatMessage, hasPredictions, predictionsCount]);

  const restaurantsTab = useMemo<TabItem>(() => {
    const label = formatMessage(messages.sgRestaurants, { count: restaurantsCount }); // prettier-ignore

    return {
      id: RESTAURANTS_TAB_ID,
      panelId: RESTAURANTS_TAB_PANEL_ID,
      label,
      isDisabled: !hasRestaurants,
    };
  }, [formatMessage, hasRestaurants, restaurantsCount]);

  const tabs = useMemo(
    () => [locationsTab, restaurantsTab],
    [locationsTab, restaurantsTab],
  );

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const syncActiveTab = useCallback(() => {
    const shouldSwitchToRestaurants = hasRestaurants && !hasPredictions && isLocationsTabActive; // prettier-ignore
    const shouldSwitchToLocations = hasPredictions && !hasRestaurants && isRestaurantsTabActive; // prettier-ignore

    if (shouldSwitchToRestaurants) {
      setActiveTabId(RESTAURANTS_TAB_ID);
    }

    if (shouldSwitchToLocations) {
      setActiveTabId(LOCATIONS_TAB_ID);
    }
  }, [
    hasPredictions,
    hasRestaurants,
    isLocationsTabActive,
    isRestaurantsTabActive,
  ]);

  // ─── Effects ─────────────────────────────────────────────────────────────────

  useEffect(syncActiveTab, [syncActiveTab, predictionsCount, restaurantsCount]);

  // ─────────────────────────────────────────────────────────────────────────────

  return {
    tabs,
    activeTabId,
    setActiveTabId,
    locationsTab,
    restaurantsTab,
    shouldRenderTabBar,
  };
};

// ─── Messages ─────────────────────────────────────────────────────────────────

const messages = defineMessages({
  locations: {
    defaultMessage: 'Locations ({count})',
    description: 'Location Search | Predictions | Locations title',
  },
  sgRestaurants: {
    defaultMessage: 'SG Restaurants ({count})',
    description: 'Location Search | Predictions | SG Restaurants title',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const LOCATIONS_TAB_ID = 'location-search.predictions.locations-tab';
const LOCATIONS_TAB_PANEL_ID = 'location-search.predictions.locations-tab-panel'; // prettier-ignore
const RESTAURANTS_TAB_ID = 'location-search.predictions.restaurants-tab';
const RESTAURANTS_TAB_PANEL_ID = 'location-search.predictions.restaurants-tab-panel'; // prettier-ignore

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  predictionsCount: number;
  restaurantsCount: number;
};
